import { Guid } from 'guid-typescript';
import { CLEAR_USER_CACHE } from '../types';
import { MeasurementCardResponse } from '../../.generated/admin-api';

import {
  CREATE_STACK,
  UPLOAD_STACK,
  UPLOAD_STACK_FAILED,
  UPLOAD_STACK_SUCCESS,
  DepartmentStackState,
  GET_MEASUREMENT_CARD,
  GET_STACKS,
  REMOVE_PICKED_STACK_NUMBER,
  StackActionTypes,
  StackState,
  UPDATE_RESERVED_STACK_NUMBERS,
  SET_FIELD_VALUES,
  CLEAR_FIELD_VALUES,
  UPDATE_FIELD_VALUE,
} from './types';

const initialState: StackState = {
  departments: {},
};

function collectBasicDataFieldValues(
  fieldValues: Record<string, unknown>,
  measurementCard?: MeasurementCardResponse,
): Record<string, unknown> {
  if (!measurementCard) {
    return {};
  }
  const basicDataFieldValues: Record<string, unknown> = {};

  for (const basicFieldId of measurementCard.basicDataFields) {
    const value = fieldValues[basicFieldId];
    if (value) {
      basicDataFieldValues[basicFieldId] = value;
    }
  }
  return basicDataFieldValues;
}

export function stackReducer(
  state = initialState,
  action: StackActionTypes,
): StackState {
  switch (action.type) {
    case GET_STACKS: {
      const departments = state.departments;
      const department: DepartmentStackState = departments[action.departmentId]
        ? departments[action.departmentId]
        : {
            organizationId: '', // TODO: must provide with stack action or remove field from department.
            localStacks: {},
            basicDataFieldValues: {},
          };
      department.stackList = action.stacks;
      department.lastUpdated = new Date().toISOString();

      return {
        ...state,
        departments: {
          ...departments,
          [action.departmentId]: department,
        },
      };
    }
    case GET_MEASUREMENT_CARD: {
      const departments = state.departments;

      departments[action.departmentId] = {
        ...departments[action.departmentId],
        measurementCard: action.measurementCard,
      };

      return {
        ...state,
        departments,
      };
    }
    case UPDATE_RESERVED_STACK_NUMBERS: {
      const departments = state.departments;
      departments[action.departmentId] = {
        ...departments[action.departmentId],
        reservedStackNumbers: action.reservedStackNumbers,
      };

      return {
        ...state,
        departments,
      };
    }
    case REMOVE_PICKED_STACK_NUMBER: {
      const departments = state.departments;

      const updatedReservedStackNumbers = departments[
        action.departmentId
      ]?.reservedStackNumbers?.filter(
        (stackNumber) => stackNumber !== action.stackNumber,
      );

      departments[action.departmentId] = {
        ...departments[action.departmentId],
        reservedStackNumbers: updatedReservedStackNumbers,
      };
      return {
        ...state,
        departments,
      };
    }
    case CREATE_STACK: {
      const id = Guid.create().toString();
      const department = state.departments[action.departmentId];
      if (!department) {
        console.error(
          'Create stack failed. Department id not found',
          action.departmentId,
        );
        return state;
      }
      department.localStacks = {
        ...department.localStacks,
        [id]: {
          id: id,
          departmentId: action.departmentId,
          measurementCardId: action.measurementCardId,
          fieldValues: action.fieldValues,
          stackNumber: action.stackNumber,
          date: action.date,
          uploadInProgress: false, // TODO: this should not be part of persistant state.
        },
      };
      department.basicDataFieldValues = collectBasicDataFieldValues(
        action.fieldValues,
        department.measurementCard,
      );
      return state;
    }
    case UPLOAD_STACK: {
      const localStack =
        state.departments[action.departmentId]?.localStacks?.[
          action.localStackId
        ];
      if (localStack) {
        localStack.uploadInProgress = true;
      }
      return state;
    }
    case UPLOAD_STACK_SUCCESS: {
      const department = state.departments[action.departmentId];
      if (department && department.localStacks) {
        delete department.localStacks[action.localStackId]; // delete localStack
        department.localStacks = { ...department.localStacks }; // trigger update
      }
      return state;
    }
    case UPLOAD_STACK_FAILED: {
      const localStack =
        state.departments[action.departmentId]?.localStacks?.[
          action.localStackId
        ];
      if (localStack) {
        localStack.uploadInProgress = false;
      }
      return state;
    }
    case SET_FIELD_VALUES: {
      const department = state.departments[action.departmentId];
      if (department) {
        department.fieldValues = action.values;
      }
      return state;
    }
    case CLEAR_FIELD_VALUES: {
      const department = state.departments[action.departmentId];
      if (department) {
        delete department.fieldValues;
      }
      return state;
    }
    case UPDATE_FIELD_VALUE: {
      const department = state.departments[action.departmentId];
      if (department) {
        if (department.fieldValues) {
          department.fieldValues = [
            ...department.fieldValues.filter(
              (fieldValue) => fieldValue.fieldId !== action.field.fieldId,
            ),
            action.field,
          ];
        } else {
          department.fieldValues = [action.field];
        }
      }
      return state;
    }
    case CLEAR_USER_CACHE:
      return initialState;
    default:
      return state;
  }
}
